import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Mail, Phone, MapPin, Users } from "lucide-react";
import Header from "../components/layout/Header";
import { StatCard, Badge, SearchInput, Button, Table } from "../components/ui/index";
import ActionMenu from "../components/ui/ActionMenu";
import AddMembreModal from "../components/modals/AddMembreModal";
import EditMembreModal from "../components/modals/EditMembreModal";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";
import { membresAPI } from "../api/services";

const roleBadgeColor = {
  "Chef de Projet": "bg-blue-100 text-blue-700",
  "Technicien IoT": "bg-purple-100 text-purple-700",
  "Responsable Technique": "bg-orange-100 text-orange-700",
};

const roleTranslationKey = {
  "Chef de Projet": "team.projectManager",
  "Technicien IoT": "team.iotTechnician",
  "Responsable Technique": "team.technicalLead",
};

export default function Equipe() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [cardFilter, setCardFilter] = useState("all");

  const [membres, setMembres] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMembres = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      if (filterStatut) params.statut = filterStatut;
      const { data } = await membresAPI.getAll(params);
      setMembres(Array.isArray(data) ? data : data.data ?? []);
    } catch (err) {
      console.error("Erreur chargement membres:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterRole, filterStatut]);

  useEffect(() => {
    fetchMembres();
  }, [fetchMembres]);

  const handleAdd = async (data) => {
    try {
      await membresAPI.create(data);
      setShowModal(false);
      fetchMembres();
    } catch (err) {
      console.error("Erreur création membre:", err);
    }
  };

  const handleEdit = async (data) => {
    try {
      await membresAPI.update(editItem._id, data);
      setEditItem(null);
      fetchMembres();
    } catch (err) {
      console.error("Erreur modification membre:", err);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await membresAPI.remove(deleteItem._id);
      setDeleteItem(null);
      fetchMembres();
    } catch (err) {
      console.error("Erreur suppression membre:", err);
    }
  };

  const actifs = membres.filter(m => m.statut === "Actif").length;

  const toggleCard = (key) => setCardFilter(prev => prev === key ? "all" : key);
  const filteredMembres = cardFilter === "all" ? membres
    : cardFilter === "actifs" ? membres.filter(m => m.statut === "Actif")
    : membres;

  const selectStyle = "px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <>
      <Header title={t('team.title')} subtitle={t('team.subtitle')} />

      {/* Stats + bouton */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <StatCard icon={Users} iconBg="bg-blue-100 text-blue-600" label={t('team.totalMembers')} value={membres.length} onClick={() => setCardFilter("all")} active={cardFilter === "all"} />
        <StatCard icon={Users} iconBg="bg-green-100 text-green-600" label={t('team.activeMembers')} value={actifs} onClick={() => toggleCard("actifs")} active={cardFilter === "actifs"} />
        <div />
        <Button onClick={() => setShowModal(true)} className="h-full justify-center gap-2">
          <Plus size={18} /> {t('team.addMember')}
        </Button>
      </div>

      {/* Recherche + filtres */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex gap-3">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher par nom, email, spécialisation, ville..." />
          </div>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className={selectStyle}>
            <option value="">{t('team.allRoles')}</option>
            <option value="Chef de Projet">{t('team.projectManager')}</option>
            <option value="Technicien IoT">{t('team.iotTechnician')}</option>
            <option value="Responsable Technique">{t('team.technicalLead')}</option>
          </select>
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className={selectStyle}>
            <option value="">{t('team.allStatuses')}</option>
            <option value="Actif">{t('team.active')}</option>
            <option value="Inactif">{t('team.inactive')}</option>
          </select>
        </div>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400 text-sm">{t('common.loading')}</div>
      ) : (
        <Table headers={[t('team.colMember'), t('team.colRole'), t('team.colContact'), t('team.colLocation'), t('team.colStatus'), t('team.colActions')]}>
          {filteredMembres.map((m) => (
            <tr key={m._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600 text-sm flex-shrink-0">
                    {m.nom.split(" ").map(n => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{m.nom}</p>
                    <p className="text-xs text-slate-400">{m._id}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${roleBadgeColor[m.role] || "bg-gray-100 text-gray-600"}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                  {roleTranslationKey[m.role] ? t(roleTranslationKey[m.role]) : m.role}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600"><Mail size={11} className="text-slate-400" />{m.email}</div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600"><Phone size={11} className="text-slate-400" />{m.telephone}</div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                  <MapPin size={13} className="text-slate-400" />
                  <div>
                    <p className="text-xs font-medium text-slate-700">{m.localisation?.split(",")[0]},</p>
                    <p className="text-xs text-slate-400">{m.localisation?.split(",")[1]?.trim()}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4"><Badge label={m.statut} /></td>
              <td className="px-6 py-4">
                <ActionMenu
                  rowId={m._id}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  onEdit={() => setEditItem(m)}
                  onDelete={() => setDeleteItem(m)}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}

      <AddMembreModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAdd={handleAdd}
      />

      <EditMembreModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        onEdit={handleEdit}
        item={editItem}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={handleDeleteConfirm}
        itemName={deleteItem?.nom}
        itemType="membre"
      />
    </>
  );
}
